import { z } from "zod";
import { zDiaCivil, zId, zInstanteUtc, zTipoItem } from "./dominio.js";
import { zMarcado } from "./estudo.js";
import { diasEntre, somarDias } from "./dia.js";
/**
 * Simulado por modelo de prova (SPEC §6 tela 7, RN-26, RN-27, RN-34).
 *
 * Este módulo é puro: nenhuma consulta, nenhuma data de relógio. Ele responde
 * três perguntas que a API e a tela fazem com as mesmas contas — que é o que
 * permite a tela conferir o número em vez de confiar nele.
 *
 * **Nada de constante de negócio aqui** (invariante 4). Peso, quantidade,
 * duração e nota de corte vêm todos do modelo de prova, que mora no banco. Nem
 * o corte tem valor padrão: um modelo sem `notaCorte` não tem corte, e o
 * relatório diz isso em vez de inventar 60%.
 */
/* ---------------------------------------------------------- modelo de prova */
/**
 * Uma linha da composição: de tal categoria, tantas questões, cada uma valendo
 * tanto. O peso é decimal em texto pela mesma razão que a nota de corte:
 * ver `zPeso`.
 */
export const zPeso = z
    .string()
    .regex(/^\d{1,4}(\.\d{1,4})?$/, "Peso precisa ser um número com até 4 casas.")
    .refine((v) => Number(v) > 0, "Peso precisa ser maior que zero.");
export const zLinhaDaComposicao = z.object({
    categoriaId: zId,
    quantidade: z.number().int().positive().max(500),
    peso: zPeso,
});
/**
 * Disponibilidade (RN-27). Nunca fixa no código: as três formas são dados.
 *
 * `dia_da_semana` usa 0=domingo a 6=sábado, na convenção do `Date` — e o dia
 * consultado é sempre o **dia do usuário** (invariante 16), nunca o UTC.
 */
export const zDisponibilidade = z.discriminatedUnion("modo", [
    z.object({ modo: z.literal("livre") }),
    z.object({ modo: z.literal("dia_da_semana"), dias: z.array(z.number().int().min(0).max(6)).min(1) }),
    z.object({ modo: z.literal("cadencia"), cadaDias: z.number().int().positive().max(365) }),
]);
export const zModeloProvaEntrada = z.object({
    nome: z.string().min(1).max(120),
    composicao: z.array(zLinhaDaComposicao).min(1, "Um modelo de prova sem composição não monta prova nenhuma."),
    duracaoMinutos: z.number().int().positive().max(1440),
    /**
     * Percentual da pontuação máxima, em texto decimal. `null` é um modelo de prova
     * **sem** corte, que é diferente de corte zero: sem corte o relatório não
     * mostra veredito de aprovação, e com corte zero qualquer nota passa.
     */
    notaCorte: z
        .string()
        .regex(/^\d{1,3}(\.\d{1,2})?$/, "Nota de corte é um percentual de 0 a 100.")
        .refine((v) => Number(v) <= 100, "Nota de corte não passa de 100%.")
        .nullable(),
    disponibilidade: zDisponibilidade,
});
export const zModeloProva = zModeloProvaEntrada.extend({
    id: zId,
    trilhaId: zId,
    /** Derivada da composição, nunca digitada. */
    pontuacaoMaxima: z.string(),
    /** Quantas questões a prova tem ao todo. Também derivada. */
    totalDeQuestoes: z.number().int().nonnegative(),
});
/* ------------------------------------------------------- pontuação derivada */
/**
 * Casas decimais que a pontuação carrega. Não é regra de negócio, é precisão de
 * representação: peso e corte já vêm limitados a 4 e 2 casas pelos schemas, e
 * somar em centésimos evita que 0.1 + 0.2 apareça como 0.30000000000000004 na
 * tela. A conta roda em inteiros e só volta a texto no fim.
 */
const CASAS = 4;
const ESCALA = 10 ** CASAS;
function paraEscala(decimalEmTexto) {
    return Math.round(Number(decimalEmTexto) * ESCALA);
}
function daEscala(inteiro) {
    const bruto = (inteiro / ESCALA).toFixed(CASAS);
    // Corta zeros à direita, mas nunca o ponto sozinho: "5.0000" vira "5".
    return bruto.replace(/\.?0+$/, "") || "0";
}
/**
 * Pontuação máxima da prova: soma de `quantidade × peso` de cada linha (RN-26).
 * É isto que torna a máxima derivada — mudar um peso no modelo de prova muda a
 * máxima sem ninguém editar um segundo número.
 */
export function pontuacaoMaxima(composicao) {
    return daEscala(composicao.reduce((soma, l) => soma + paraEscala(l.peso) * l.quantidade, 0));
}
export function totalDeQuestoes(composicao) {
    return composicao.reduce((soma, l) => soma + l.quantidade, 0);
}
/**
 * O corte em pontos, derivado do percentual e da máxima (RN-26). Devolve `null`
 * quando o modelo de prova não tem corte — sem corte não há veredito, e forçar um
 * número aqui seria exatamente a constante que a RN-26 proíbe.
 */
export function corteEmPontos(composicao, notaCorte) {
    if (notaCorte === null)
        return null;
    const maxima = paraEscala(pontuacaoMaxima(composicao));
    return daEscala(Math.round((maxima * Number(notaCorte)) / 100));
}
/* ----------------------------------------------------------- disponibilidade */
export const zDisponibilidadeHoje = z.object({
    liberado: z.boolean(),
    /** Frase pronta para a tela. Sempre presente, inclusive quando liberado. */
    motivo: z.string(),
    /** Quando volta a liberar, no modo cadência. `null` nos outros. */
    liberaEm: zDiaCivil.nullable(),
});
const NOME_DO_DIA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
/**
 * Se a prova pode começar hoje (RN-27).
 *
 * `hoje` já é o dia do usuário e `ultimoInicio` é o dia do usuário do último
 * simulado iniciado — a conversão acontece antes, em quem chama, porque é ela
 * que conhece o fuso (invariante 16).
 */
export function disponibilidadeHoje(d, hoje, ultimoInicio) {
    if (d.modo === "livre") {
        return { liberado: true, motivo: "Disponível a qualquer dia.", liberaEm: null };
    }
    if (d.modo === "dia_da_semana") {
        // `hoje` é AAAA-MM-DD do usuário; o `T12:00:00Z` evita que o fuso do
        // ambiente empurre a data para o dia anterior ao descobrir o dia da semana.
        const diaDaSemana = new Date(`${hoje}T12:00:00Z`).getUTCDay();
        const nomes = [...d.dias].sort().map((n) => NOME_DO_DIA[n]).join(", ");
        return d.dias.includes(diaDaSemana)
            ? { liberado: true, motivo: `Hoje é ${NOME_DO_DIA[diaDaSemana]}, dia de prova.`, liberaEm: null }
            : { liberado: false, motivo: `Este simulado abre ${nomes === "" ? "nunca" : `a${nomes.length > 9 ? "s" : ""} ${nomes}`}.`, liberaEm: null };
    }
    if (ultimoInicio === null) {
        return { liberado: true, motivo: "Você ainda não fez este simulado.", liberaEm: null };
    }
    const desde = diasEntre(ultimoInicio, hoje);
    if (desde >= d.cadaDias) {
        return { liberado: true, motivo: `Último foi há ${desde} dia(s).`, liberaEm: null };
    }
    const faltam = d.cadaDias - desde;
    return {
        liberado: false,
        motivo: `Este simulado repete a cada ${d.cadaDias} dia(s). Faltam ${faltam}.`,
        liberaEm: somarDias(ultimoInicio, d.cadaDias),
    };
}
/* ---------------------------------------------------------------- relatório */
/**
 * O que a prova devolve. A RN-26 pede pontuação por peso e corte derivado; o
 * relatório existe para responder **onde eu estou fraco**, então a quebra por
 * categoria não é enfeite — é o motivo de a tela existir.
 */
export const zLinhaDoRelatorio = z.object({
    categoriaId: zId,
    caminho: z.array(z.string()),
    peso: z.string(),
    /** Quantas o modelo de prova pedia. Pode ser maior que `respondidas`. */
    previstas: z.number().int().nonnegative(),
    /** Quantas entraram de fato: o acervo pode não ter tido o bastante. */
    sorteadas: z.number().int().nonnegative(),
    respondidas: z.number().int().nonnegative(),
    acertos: z.number().int().nonnegative(),
    /** `acertos × peso`. */
    pontos: z.string(),
    /** `sorteadas × peso`: o teto que esta categoria podia dar nesta prova. */
    pontosPossiveis: z.string(),
});
/**
 * Uma questão da prova, no relatório — RN-34.
 *
 * **É aqui que a prova é corrigida.** Durante a execução o simulado não revela
 * nada (ADR 021): tirar a correção da execução sem trazê-la para cá não teria
 * mudado o gabarito de lugar, teria apagado ele.
 *
 * A forma é a mesma do `zErroDiagnosticado` da RN-46, e de propósito: as duas
 * telas respondem a mesma pergunta — "o que eu marquei, o que era, por quê" — e
 * duas formas diferentes para isso seriam duas manutenções.
 */
export const zQuestaoRevisada = z.object({
    itemId: zId,
    /** A ordem em que ela caiu na prova, de 1 em diante. */
    numero: z.number().int().positive(),
    tipo: zTipoItem,
    enunciado: z.string(),
    categoria: z.array(z.string()),
    peso: z.string(),
    explicacao: z.string().nullable(),
    /** Sem elas, "marcou C" não diz o que é C. */
    alternativas: z.record(z.string(), z.string()).nullable(),
    /**
     * `null` no cartão: não há certo objetivo, e quem julga é o usuário (mesma
     * decisão da RN-46 — item sem gabarito não recebe rótulo vazio).
     */
    gabarito: z.unknown(),
    /** `null` na questão que ficou em branco. */
    marcado: zMarcado.nullable(),
    respondida: z.boolean(),
    acertou: z.boolean(),
});
export const zRelatorioDeSimulado = z.object({
    simuladoId: zId,
    modeloProvaNome: z.string(),
    iniciadoEm: zInstanteUtc,
    /**
     * O dia **do usuário** em que a prova começou, convertido pelo servidor.
     * A tela não recorta o ISO: `iniciadoEm.slice(0, 10)` seria o dia UTC, e às
     * 22h de São Paulo a prova apareceria como sendo de amanhã (invariante 16).
     */
    dia: zDiaCivil,
    finalizadoEm: zInstanteUtc.nullable(),
    /** Tempo de parede, do início ao fim. `null` enquanto não terminou. */
    duracaoMs: z.number().int().nonnegative().nullable(),
    duracaoMinutos: z.number().int().positive(),
    /** Se o tempo acabou antes de o usuário entregar. */
    estourouOTempo: z.boolean(),
    questoes: z.number().int().nonnegative(),
    respondidas: z.number().int().nonnegative(),
    acertos: z.number().int().nonnegative(),
    pontuacao: z.string(),
    /** Máxima **da prova montada**, não a do modelo de prova: ver `pontuar`. */
    pontuacaoMaxima: z.string(),
    /** Máxima que o modelo de prova pedia, para a tela poder explicar a diferença. */
    pontuacaoMaximaPrevista: z.string(),
    percentual: z.string(),
    /**
     * A prova questão a questão, na ordem em que caiu. Vazia enquanto a prova
     * não terminou: durante a execução não há correção nenhuma (ADR 021).
     */
    itens: z.array(zQuestaoRevisada),
    corte: z.string().nullable(),
    cortePercentual: z.string().nullable(),
    /** `null` quando o modelo de prova não tem corte: sem corte não há veredito. */
    passou: z.boolean().nullable(),
    /** Ordenada por pontos perdidos: a categoria que mais custou vem primeiro. */
    porCategoria: z.array(zLinhaDoRelatorio),
});
/**
 * A conta inteira do resultado.
 *
 * A decisão que importa aqui: a **máxima da prova** é `sorteadas × peso`, não
 * `previstas × peso`. Se o acervo só tinha 8 questões numa categoria que o
 * modelo de prova pedia 10, o usuário não pode ser punido por 2 questões que
 * nunca lhe foram mostradas — o percentual sairia menor que o desempenho real e
 * o corte ficaria impossível. A máxima prevista vai junto no relatório para a
 * tela poder dizer que a prova saiu menor do que o modelo pedia.
 *
 * Questão não respondida (tempo acabou) conta como erro: ela foi sorteada,
 * entra na máxima e não dá ponto. Deixar em branco não é o mesmo que não ter
 * sido perguntado.
 */
export function pontuar(e) {
    const porCategoria = e.composicao.map((linha) => {
        const daCategoria = e.itens.filter((i) => i.categoriaId === linha.categoriaId);
        const acertos = daCategoria.filter((i) => i.respondida && i.acertou).length;
        const peso = paraEscala(linha.peso);
        return {
            categoriaId: linha.categoriaId,
            caminho: e.caminhos[linha.categoriaId] ?? [],
            peso: linha.peso,
            previstas: linha.quantidade,
            sorteadas: daCategoria.length,
            respondidas: daCategoria.filter((i) => i.respondida).length,
            acertos,
            pontos: daEscala(peso * acertos),
            pontosPossiveis: daEscala(peso * daCategoria.length),
        };
    });
    const pontos = porCategoria.reduce((s, l) => s + paraEscala(l.pontos), 0);
    const maxima = porCategoria.reduce((s, l) => s + paraEscala(l.pontosPossiveis), 0);
    const corte = e.notaCorte === null ? null : Math.round((maxima * Number(e.notaCorte)) / 100);
    return {
        questoes: e.itens.length,
        respondidas: e.itens.filter((i) => i.respondida).length,
        acertos: e.itens.filter((i) => i.respondida && i.acertou).length,
        pontuacao: daEscala(pontos),
        pontuacaoMaxima: daEscala(maxima),
        pontuacaoMaximaPrevista: pontuacaoMaxima(e.composicao),
        // Percentual só existe se a prova valia alguma coisa: 0 de 0 não é 0%.
        percentual: maxima === 0 ? "0" : ((pontos / maxima) * 100).toFixed(1),
        corte: corte === null ? null : daEscala(corte),
        cortePercentual: e.notaCorte,
        passou: corte === null ? null : pontos >= corte,
        porCategoria: [...porCategoria].sort((a, b) => paraEscala(b.pontosPossiveis) - paraEscala(b.pontos) - (paraEscala(a.pontosPossiveis) - paraEscala(a.pontos))),
    };
}
/* ------------------------------------------------------- listas e execução */
/** A composição com o nome de cada categoria já resolvido, para a lista. */
export const zLinhaComCaminho = zLinhaDaComposicao.extend({
    caminho: z.array(z.string()),
    /** Quantos itens vivos o acervo tem nesta categoria, com descendentes. */
    disponiveis: z.number().int().nonnegative(),
});
export const zModeloProvaNaLista = zModeloProva.extend({
    /** A composição resolvida: a tela nunca precisa mostrar um id. */
    composicaoResolvida: z.array(zLinhaComCaminho),
    /** Dia do usuário do último início, ou `null` se nunca foi feito. */
    ultimoInicio: zDiaCivil.nullable(),
    disponibilidade_hoje: zDisponibilidadeHoje,
    /**
     * Quantos itens vivos o acervo tem para cada categoria da composição. É o que
     * deixa a tela avisar **antes** que a prova vai sair menor do que o pedido,
     * em vez de o usuário descobrir na questão 8 de 10.
     */
    disponiveisPorCategoria: z.record(z.string(), z.number().int().nonnegative()),
});
export const zSimuladoAberto = z.object({
    simuladoId: zId,
    sessaoId: zId,
    modeloProvaNome: z.string(),
    iniciadoEm: zInstanteUtc,
    duracaoMinutos: z.number().int().positive(),
    /**
     * Quando o tempo acaba, em UTC, calculado **pelo servidor**. O cronômetro da
     * tela conta até aqui: relógio de navegador atrasado ou adiantado não pode
     * dar nem tirar tempo de prova.
     */
    prazoEm: zInstanteUtc,
    questoes: z.number().int().nonnegative(),
});
export const zSimuladoNaLista = z.object({
    simuladoId: zId,
    modeloProvaNome: z.string(),
    dia: zDiaCivil,
    finalizado: z.boolean(),
    questoes: z.number().int().nonnegative(),
    pontuacao: z.string().nullable(),
    pontuacaoMaxima: z.string(),
    percentual: z.string().nullable(),
    notaCorte: z.string().nullable(),
    passou: z.boolean().nullable(),
});
export const zMontarSimulado = z.object({
    simuladoId: zId,
    sessaoId: zId,
    questoes: z.number().int().nonnegative(),
    /** Linhas que o acervo não conseguiu preencher, com o que faltou. */
    faltou: z.array(zLinhaDaComposicao),
});
export const zPainelDoSimulado = z.object({
    modelosProva: z.array(zModeloProvaNaLista),
    aberto: zSimuladoAberto.nullable(),
    historico: z.array(zSimuladoNaLista),
});
/** Alvo de rota com `:id`. Um por módulo, como nos outros (ADR 005). */
export const zModeloProvaId = z.object({ id: zId });
export const zSimuladoId = z.object({ id: zId });
//# sourceMappingURL=simulado.js.map